import React, { useState } from 'react';
import { send } from 'emailjs-com';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { interestOptions } from '../../content/site';

const SERVICE_ID = 'service_kx1sqjq';
const TEMPLATE_ID = 'template_unjwjfs';
const PUBLIC_KEY = 'NGGTeRsqIpCieoKvE';

type ContactFields = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  interest: string;
  message: string;
};

const emptyFields: ContactFields = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  interest: '',
  message: '',
};

type FormStatus = 'idle' | 'sending' | 'sent' | 'error';

/** Formats up to 10 digits as (555) 123-4567. */
export const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  const area = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const line = digits.slice(6, 10);

  if (digits.length <= 3) {
    return area;
  }
  if (digits.length <= 6) {
    return `(${area}) ${prefix}`;
  }
  return `(${area}) ${prefix}-${line}`;
};

type ContactFormProps = {
  heading?: string;
  intro?: string;
  embedded?: boolean;
};

/** Inquiry form. Confirmation is shown only after EmailJS accepts the message. */
const ContactForm = ({
  heading = 'Tell us about the work',
  intro = 'A short description is enough to start. We read every note and reply directly.',
  embedded = false,
}: ContactFormProps) => {
  const [fields, setFields] = useState<ContactFields>(emptyFields);
  const [status, setStatus] = useState<FormStatus>('idle');

  const update = (name: keyof ContactFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = name === 'phone' ? formatPhone(event.target.value) : event.target.value;
    setFields((current) => ({ ...current, [name]: value }));
  };

  const submitContactInfo = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('sending');

    try {
      await send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          firstName: fields.firstName,
          lastName: fields.lastName,
          email: fields.email,
          reply_to: fields.email,
          phone: fields.phone,
          company: fields.company,
          message: `Need: ${fields.interest}\n\n${fields.message}`,
        },
        PUBLIC_KEY
      );
      setFields(emptyFields);
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <Box
      component="section"
      aria-labelledby={embedded ? undefined : 'contact-form-heading'}
      sx={{ maxWidth: 720, mx: embedded ? 0 : 'auto' }}
    >
      {!embedded && (
        <>
          <Typography id="contact-form-heading" variant="h2" sx={{ fontSize: { xs: '1.75rem', md: '2rem' }, textAlign: 'center' }}>
            {heading}
          </Typography>
          <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 1.5, mb: 3 }}>
            {intro}
          </Typography>
        </>
      )}
      {embedded && (
        <Typography color="text.secondary" sx={{ maxWidth: 680, fontSize: '1.125rem', lineHeight: 1.6, mb: 3 }}>
          {intro}
        </Typography>
      )}

      <Box
        component="form"
        onSubmit={submitContactInfo}
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        }}
      >
        <TextField
          id="first-name"
          name="first-name"
          label="First name"
          autoComplete="given-name"
          value={fields.firstName}
          onChange={update('firstName')}
          required
        />
        <TextField
          id="last-name"
          name="last-name"
          label="Last name"
          autoComplete="family-name"
          value={fields.lastName}
          onChange={update('lastName')}
          required
        />
        <TextField
          id="email"
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          value={fields.email}
          onChange={update('email')}
          required
          inputProps={{ pattern: '[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$' }}
        />
        <TextField
          id="phone"
          name="phone"
          label="Phone"
          type="tel"
          autoComplete="tel"
          value={fields.phone}
          onChange={update('phone')}
          required
          inputProps={{ pattern: '^\\(\\d{3}\\)\\s\\d{3}-\\d{4}$' }}
        />
        <TextField
          id="company"
          name="company"
          label="Company"
          autoComplete="organization"
          value={fields.company}
          onChange={update('company')}
          sx={{ gridColumn: { sm: '1 / -1' } }}
        />
        <TextField
          id="interest"
          name="interest"
          label="What do you need?"
          select
          required
          value={fields.interest}
          onChange={update('interest')}
          sx={{ gridColumn: { sm: '1 / -1' } }}
        >
          {interestOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          id="project-description"
          name="project-description"
          label="Project description"
          value={fields.message}
          onChange={update('message')}
          required
          multiline
          minRows={4}
          sx={{ gridColumn: { sm: '1 / -1' } }}
        />

        <Button
          id="submit-contact-info"
          type="submit"
          variant="contained"
          disabled={status === 'sending'}
          sx={{ gridColumn: { sm: '1 / -1' }, justifySelf: 'start', px: 3 }}
        >
          {status === 'sending' ? 'Sending…' : 'Send'}
        </Button>
      </Box>

      {status === 'sent' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Thank you. We have your note and will reply directly.
        </Alert>
      )}
      {status === 'error' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          We could not send that. Check the form and try again.
        </Alert>
      )}
    </Box>
  );
};

export default ContactForm;
